// Calendar export helpers — produce correct ICS files and Google/Outlook
// "add event" URLs from a few common date input formats.

const MONTHS = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

// Parse a date input into { year, month, day, hours, minutes, hasTime }.
// Returns null when input is missing or unrecognisable (e.g. "xxx").
export function parseEventDate(input, fallbackTime) {
  if (!input || typeof input !== "string") return null;
  const raw = input.trim();
  if (!raw || /^x{2,}$/i.test(raw)) return null;

  // 1) ISO-ish: "YYYY-MM-DD" optionally with time "HH:MM" or "HH:MM AM/PM"
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?:\s*(AM|PM))?)?$/i);
  if (m) {
    const [, y, mo, d, hh, mm, ap] = m;
    const out = {
      year: +y,
      month: +mo - 1,
      day: +d,
      hours: 0,
      minutes: 0,
      hasTime: !!hh,
    };
    if (hh) {
      let h = +hh;
      if (ap) {
        const a = ap.toUpperCase();
        if (a === "PM" && h !== 12) h += 12;
        if (a === "AM" && h === 12) h = 0;
      }
      out.hours = h;
      out.minutes = +mm;
    }
    return applyFallbackTime(out, fallbackTime);
  }

  // 2) Friendly: "18th May 2026" or "18th May 2026, 09:30 AM"
  const cleaned = raw.replace(/(\d+)(st|nd|rd|th)/gi, "$1");
  m = cleaned.match(
    /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})(?:[,\s]+(\d{1,2}):(\d{2})\s*(AM|PM)?)?$/i
  );
  if (m) {
    const [, d, monName, y, hh, mm, ap] = m;
    const monthIdx = MONTHS[monName.toLowerCase()];
    if (monthIdx === undefined) return null;
    const out = {
      year: +y,
      month: monthIdx,
      day: +d,
      hours: 0,
      minutes: 0,
      hasTime: !!hh,
    };
    if (hh) {
      let h = +hh;
      if (ap) {
        const a = ap.toUpperCase();
        if (a === "PM" && h !== 12) h += 12;
        if (a === "AM" && h === 12) h = 0;
      }
      out.hours = h;
      out.minutes = +mm;
    }
    return applyFallbackTime(out, fallbackTime);
  }

  return null;
}

function applyFallbackTime(parsed, fallbackTime) {
  if (parsed.hasTime || !fallbackTime || typeof fallbackTime !== "string")
    return parsed;
  const m = fallbackTime
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return parsed;
  let h = +m[1];
  if (m[3]) {
    const a = m[3].toUpperCase();
    if (a === "PM" && h !== 12) h += 12;
    if (a === "AM" && h === 12) h = 0;
  }
  return { ...parsed, hours: h, minutes: +m[2], hasTime: true };
}

// Format a Date for ICS local-floating time: YYYYMMDDTHHMMSS (no TZ marker)
function formatLocalDateTime(p) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}${pad(p.month + 1)}${pad(p.day)}T${pad(p.hours)}${pad(
    p.minutes
  )}00`;
}

// Format an all-day date as YYYYMMDD (used with VALUE=DATE)
function formatDateOnly(p) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${p.year}${pad(p.month + 1)}${pad(p.day)}`;
}

// Add one day (used because ICS DTEND for all-day events is exclusive)
function addOneDay(p) {
  const d = new Date(p.year, p.month, p.day + 1);
  return {
    year: d.getFullYear(),
    month: d.getMonth(),
    day: d.getDate(),
    hours: 0,
    minutes: 0,
    hasTime: false,
  };
}

// Escape ICS text per RFC 5545 §3.3.11
function icsEscape(text) {
  if (text == null) return "";
  return String(text)
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

// Encode dates for Google Calendar URLs.
// Google supports both YYYYMMDDTHHMMSSZ (UTC) and YYYYMMDD (all-day).
function googleDate(p, allDay) {
  const pad = (n) => String(n).padStart(2, "0");
  if (allDay) {
    return `${p.year}${pad(p.month + 1)}${pad(p.day)}`;
  }
  // Build a Date in local time then convert to UTC for Google
  const dt = new Date(p.year, p.month, p.day, p.hours, p.minutes, 0);
  const y = dt.getUTCFullYear();
  const mo = pad(dt.getUTCMonth() + 1);
  const d = pad(dt.getUTCDate());
  const h = pad(dt.getUTCHours());
  const mi = pad(dt.getUTCMinutes());
  return `${y}${mo}${d}T${h}${mi}00Z`;
}

// Encode dates for Outlook web URLs.
// Outlook expects ISO-like local time without seconds: YYYY-MM-DDTHH:MM
function outlookDate(p, allDay) {
  const pad = (n) => String(n).padStart(2, "0");
  if (allDay) {
    return `${p.year}-${pad(p.month + 1)}-${pad(p.day)}`;
  }
  return `${p.year}-${pad(p.month + 1)}-${pad(p.day)}T${pad(p.hours)}:${pad(
    p.minutes
  )}`;
}

// Build a CalendarEvent struct from raw event fields.
// `event` is expected to provide: title, description, location, start, end,
// optional startTime/endTime override strings.
export function buildCalendarEvent(event) {
  const start = parseEventDate(event.start, event.startTime);
  let end = parseEventDate(event.end, event.endTime);
  if (!start) return null;

  const allDay = !start.hasTime;

  // If end is missing, default to start
  if (!end) end = { ...start };

  // For all-day events with same start and end day, ICS DTEND is exclusive,
  // so Google/Outlook/ICS need end = start + 1 day to span the full day.
  let icsEnd = end;
  if (allDay) {
    // Make sure end is not before start
    const startMs = new Date(start.year, start.month, start.day).getTime();
    const endMs = new Date(end.year, end.month, end.day).getTime();
    if (endMs < startMs) icsEnd = { ...start };
    icsEnd = addOneDay(icsEnd);
  }

  return {
    title: event.title || "Event",
    description: event.description || "",
    location: event.location || "",
    allDay,
    start,
    end: icsEnd,
    rawEnd: end,
    id: event.id ?? Math.random().toString(36).slice(2),
  };
}

// Generate an ICS file body. Returns null if the event doesn't have a usable
// start date (so the UI can disable the buttons gracefully).
export function buildICS(event) {
  const ce = buildCalendarEvent(event);
  if (!ce) return null;

  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const dtstamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(
    now.getUTCDate()
  )}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(
    now.getUTCSeconds()
  )}Z`;

  const dtstart = ce.allDay
    ? `DTSTART;VALUE=DATE:${formatDateOnly(ce.start)}`
    : `DTSTART:${formatLocalDateTime(ce.start)}`;
  const dtend = ce.allDay
    ? `DTEND;VALUE=DATE:${formatDateOnly(ce.end)}`
    : `DTEND:${formatLocalDateTime(ce.end)}`;

  // RFC 5545 requires CRLF line endings
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//KPMG Africa CoE//Events//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${ce.id}@kpmg-africa-coe`,
    `DTSTAMP:${dtstamp}`,
    dtstart,
    dtend,
    `SUMMARY:${icsEscape(ce.title)}`,
    `LOCATION:${icsEscape(ce.location)}`,
    `DESCRIPTION:${icsEscape(ce.description)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

export function buildGoogleCalendarUrl(event) {
  const ce = buildCalendarEvent(event);
  if (!ce) return null;
  const dates = `${googleDate(ce.start, ce.allDay)}/${googleDate(
    ce.end,
    ce.allDay
  )}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ce.title,
    dates,
    details: ce.description,
    location: ce.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function buildOutlookCalendarUrl(event) {
  const ce = buildCalendarEvent(event);
  if (!ce) return null;
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: ce.title,
    body: ce.description,
    location: ce.location,
    startdt: outlookDate(ce.start, ce.allDay),
    enddt: outlookDate(ce.rawEnd, ce.allDay),
    allday: ce.allDay ? "true" : "false",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function buildYahooCalendarUrl(event) {
  const ce = buildCalendarEvent(event);
  if (!ce) return null;
  const params = new URLSearchParams({
    v: "60",
    title: ce.title,
    desc: ce.description,
    in_loc: ce.location,
    st: googleDate(ce.start, ce.allDay),
    et: googleDate(ce.end, ce.allDay),
    dur: ce.allDay ? "allday" : "",
  });
  return `https://calendar.yahoo.com/?${params.toString()}`;
}

export function downloadICS(event) {
  const body = buildICS(event);
  if (!body) return false;
  const blob = new Blob([body], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(event.title || "event").replace(/[^\w-]+/g, "_")}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}

// Returns true when start/end are usable (helpful for disabling UI)
export function eventHasValidDates(event) {
  return parseEventDate(event.start, event.startTime) !== null;
}

// Returns "upcoming" | "ongoing" | "past" based on today vs event dates
export function getEventStatus(event, now = new Date()) {
  const start = parseEventDate(event.start, event.startTime);
  if (!start) return "unknown";
  const end = parseEventDate(event.end, event.endTime) || start;
  const startDt = new Date(
    start.year,
    start.month,
    start.day,
    start.hours,
    start.minutes
  );
  const endDt = new Date(
    end.year,
    end.month,
    end.day,
    end.hasTime ? end.hours : 23,
    end.hasTime ? end.minutes : 59
  );
  if (now < startDt) return "upcoming";
  if (now > endDt) return "past";
  return "ongoing";
}
